// src/pages/support/ReportProblem.tsx

import React, {
  FormEvent,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import "./SupportPages.css";


// =====================================================
// CONFIGURATION
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

interface SupportTicket {
  id: string;
  ticketNumber: string;
  status: string;
  createdAt: string;
}

interface ProblemReportResponse {
  success: boolean;
  message: string;
  ticket?: SupportTicket;
}


// =====================================================
// REPORT A PROBLEM
// =====================================================

const ReportProblem: React.FC = () => {

  const [
    name,
    setName,
  ] = useState("");

  const [
    email,
    setEmail,
  ] = useState("");

  const [
    problemType,
    setProblemType,
  ] = useState("");

  const [
    game,
    setGame,
  ] = useState("");

  const [
    device,
    setDevice,
  ] = useState("");

  const [
    title,
    setTitle,
  ] = useState("");

  const [
    description,
    setDescription,
  ] = useState("");

  const [
    steps,
    setSteps,
  ] = useState("");

  const [
    roomId,
    setRoomId,
  ] = useState("");

  const [
    matchId,
    setMatchId,
  ] = useState("");

  const [
    notice,
    setNotice,
  ] = useState("");

  const [
    ticketNumber,
    setTicketNumber,
  ] = useState("");

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const [
    submitStatus,
    setSubmitStatus,
  ] = useState<
    "idle" |
    "success" |
    "error"
  >("idle");


  // ===================================================
  // SUBMIT
  // ===================================================

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>
  ) => {

    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setNotice("");
    setTicketNumber("");

    try {

      const response =
        await fetch(
          `${API_BASE_URL}/api/support/report`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              ...(
                localStorage.getItem("token")
                  ? {
                      Authorization:
                        `Bearer ${localStorage.getItem("token")}`,
                    }
                  : {}
              ),
            },

            body: JSON.stringify({
              name:
                name.trim(),

              email:
                email.trim(),

              problemType,

              gameName:
                game.trim(),

              device,

              title:
                title.trim(),

              description:
                description.trim(),

              stepsToReproduce:
                steps.trim(),

              roomId:
                roomId.trim(),

              matchId:
                matchId.trim(),
            }),
          }
        );


      const data:
        ProblemReportResponse =
          await response.json();


      if (
        !response.ok ||
        !data.success
      ) {

        throw new Error(
          data.message ||
          "Unable to submit your problem report."
        );
      }


      // =================================================
      // SUCCESS
      // =================================================

      setSubmitStatus(
        "success"
      );

      setNotice(
        data.message ||
        "Your problem report has been submitted to P-ARENA Support."
      );


      if (
        data.ticket?.ticketNumber
      ) {

        setTicketNumber(
          data.ticket.ticketNumber
        );
      }


      // =================================================
      // CLEAR FORM
      // =================================================

      setName("");
      setEmail("");
      setProblemType("");
      setGame("");
      setDevice("");
      setTitle("");
      setDescription("");
      setSteps("");
      setRoomId("");
      setMatchId("");

    } catch (error) {

      console.error(
        "Problem report error:",
        error
      );


      setSubmitStatus(
        "error"
      );


      setNotice(
        error instanceof Error
          ? error.message
          : "Unable to submit your problem report. Please try again."
      );

    } finally {

      setIsSubmitting(
        false
      );
    }
  };


  // ===================================================
  // UI
  // ===================================================

  return (
    <main className="support-page">

      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="support-hero support-hero-compact">

        <div className="support-hero-glow" />

        <div className="support-hero-content">

          <nav
            className="support-breadcrumb"
            aria-label="Breadcrumb"
          >

            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Support
            </span>

            <span>/</span>

            <strong>
              Report a Problem
            </strong>

          </nav>


          <div className="support-emblem">
            !
          </div>

          <p className="support-eyebrow">
            P-ARENA SUPPORT
          </p>

          <h1>
            Report a Problem
          </h1>

          <p className="support-hero-description">
            Found a bug, gameplay issue,
            account problem, or unexpected
            behavior? Give us the details needed
            to investigate it.
          </p>

        </div>

      </section>


      {/* ============================================= */}
      {/* CONTENT                                       */}
      {/* ============================================= */}

      <div className="support-content">

        <section className="support-report-intro">

          <div>

            <span>
              01
            </span>

            <h3>
              Tell Us What Happened
            </h3>

            <p>
              Describe the problem clearly and
              explain what you expected to happen.
            </p>

          </div>


          <div>

            <span>
              02
            </span>

            <h3>
              Include the Steps
            </h3>

            <p>
              Tell us what you did immediately
              before the problem occurred.
            </p>

          </div>


          <div>

            <span>
              03
            </span>

            <h3>
              Include Your Environment
            </h3>

            <p>
              Device and game information can
              make technical problems easier
              to reproduce.
            </p>

          </div>

        </section>


        {/* =========================================== */}
        {/* REPORT FORM                                 */}
        {/* =========================================== */}

        <section className="support-form-card support-report-form">

          <div className="support-form-heading">

            <small>
              TECHNICAL REPORT
            </small>

            <h2>
              Problem Details
            </h2>

            <p>
              Please provide as much useful
              information as possible.
            </p>

          </div>


          <form
            className="support-form"
            onSubmit={handleSubmit}
          >

            {/* ======================================= */}
            {/* CONTACT INFORMATION                     */}
            {/* ======================================= */}

            <div className="support-form-row">

              <div className="support-field">

                <label htmlFor="problem-name">
                  Name
                </label>

                <input
                  id="problem-name"
                  type="text"
                  value={name}
                  onChange={(event) =>
                    setName(
                      event.target.value
                    )
                  }
                  placeholder="Your name"
                  maxLength={100}
                  disabled={isSubmitting}
                  required
                />

              </div>


              <div className="support-field">

                <label htmlFor="problem-email">
                  Email
                </label>

                <input
                  id="problem-email"
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(
                      event.target.value
                    )
                  }
                  placeholder="you@example.com"
                  maxLength={255}
                  disabled={isSubmitting}
                  required
                />

              </div>

            </div>


            {/* ======================================= */}
            {/* PROBLEM TYPE / GAME                     */}
            {/* ======================================= */}

            <div className="support-form-row">

              <div className="support-field">

                <label htmlFor="problem-type">
                  Problem Type
                </label>

                <select
                  id="problem-type"
                  value={problemType}
                  onChange={(event) =>
                    setProblemType(
                      event.target.value
                    )
                  }
                  disabled={isSubmitting}
                  required
                >

                  <option value="">
                    Select problem type
                  </option>

                  <option value="gameplay">
                    Gameplay Problem
                  </option>

                  <option value="score">
                    Score / Game Result
                  </option>

                  <option value="ai">
                    AI Opponent
                  </option>

                  <option value="multiplayer">
                    Multiplayer
                  </option>

                  <option value="account">
                    Account
                  </option>

                  <option value="profile">
                    Profile / Statistics
                  </option>

                  <option value="login">
                    Login / Registration
                  </option>

                  <option value="display">
                    Display / Interface
                  </option>

                  <option value="performance">
                    Performance
                  </option>

                  <option value="other">
                    Other
                  </option>

                </select>

              </div>


              <div className="support-field">

                <label htmlFor="problem-game">
                  Game
                </label>

                <select
                  id="problem-game"
                  value={game}
                  onChange={(event) =>
                    setGame(
                      event.target.value
                    )
                  }
                  disabled={isSubmitting}
                >

                  <option value="">
                    Not game-specific
                  </option>

                  <option value="Fasoor">
                    Fasoor
                  </option>

                  <option value="Tika">
                    Tika
                  </option>

                  <option value="Bitruno">
                    Bitruno
                  </option>

                  <option value="Charwali">
                    Charwali
                  </option>

                  <option value="Fiskoot">
                    Fiskoot
                  </option>

                </select>

              </div>

            </div>


            {/* ======================================= */}
            {/* DEVICE                                  */}
            {/* ======================================= */}

            <div className="support-field">

              <label htmlFor="problem-device">
                Device / Platform
              </label>

              <select
                id="problem-device"
                value={device}
                onChange={(event) =>
                  setDevice(
                    event.target.value
                  )
                }
                disabled={isSubmitting}
                required
              >

                <option value="">
                  Select your device
                </option>

                <option value="windows">
                  Windows Computer
                </option>

                <option value="mac">
                  Mac
                </option>

                <option value="linux">
                  Linux Computer
                </option>

                <option value="iphone">
                  iPhone
                </option>

                <option value="android">
                  Android Phone
                </option>

                <option value="ipad">
                  iPad / Tablet
                </option>

                <option value="other">
                  Other
                </option>

              </select>

            </div>


            {/* ======================================= */}
            {/* TITLE                                   */}
            {/* ======================================= */}

            <div className="support-field">

              <label htmlFor="problem-title">
                Problem Title
              </label>

              <input
                id="problem-title"
                type="text"
                value={title}
                onChange={(event) =>
                  setTitle(
                    event.target.value
                  )
                }
                placeholder="Example: Game did not end after winning score"
                maxLength={150}
                disabled={isSubmitting}
                required
              />

            </div>


            {/* ======================================= */}
            {/* DESCRIPTION                             */}
            {/* ======================================= */}

            <div className="support-field">

              <label htmlFor="problem-description">
                What Happened?
              </label>

              <textarea
                id="problem-description"
                value={description}
                onChange={(event) =>
                  setDescription(
                    event.target.value
                  )
                }
                placeholder="Describe what happened and what you expected to happen..."
                rows={7}
                maxLength={4000}
                disabled={isSubmitting}
                required
              />

              <span className="support-character-count">
                {description.length} / 4000
              </span>

            </div>


            {/* ======================================= */}
            {/* STEPS                                   */}
            {/* ======================================= */}

            <div className="support-field">

              <label htmlFor="problem-steps">
                Steps to Reproduce
              </label>

              <textarea
                id="problem-steps"
                value={steps}
                onChange={(event) =>
                  setSteps(
                    event.target.value
                  )
                }
                placeholder={
                  "1. I opened Fasoor\n2. I selected Play with AI\n3. I played...\n4. The problem occurred..."
                }
                rows={7}
                maxLength={3000}
                disabled={isSubmitting}
              />

              <span className="support-character-count">
                {steps.length} / 3000
              </span>

            </div>


            {/* ======================================= */}
            {/* GAME REFERENCES                         */}
            {/* ======================================= */}

            <div className="support-form-row">

              <div className="support-field">

                <label htmlFor="problem-room-id">
                  Room ID
                </label>

                <input
                  id="problem-room-id"
                  type="text"
                  value={roomId}
                  onChange={(event) =>
                    setRoomId(
                      event.target.value
                    )
                  }
                  placeholder="Optional"
                  maxLength={100}
                  disabled={isSubmitting}
                />

              </div>


              <div className="support-field">

                <label htmlFor="problem-match-id">
                  Match ID
                </label>

                <input
                  id="problem-match-id"
                  type="text"
                  value={matchId}
                  onChange={(event) =>
                    setMatchId(
                      event.target.value
                    )
                  }
                  placeholder="Optional"
                  maxLength={100}
                  disabled={isSubmitting}
                />

              </div>

            </div>


            {/* ======================================= */}
            {/* TIP                                     */}
            {/* ======================================= */}

            <div className="support-report-tip">

              <span>
                ♣
              </span>

              <div>

                <strong>
                  Helpful Report
                </strong>

                <p>
                  Include exact scores, the game
                  mode, what you clicked, and any
                  error message you saw. Avoid
                  including passwords or other
                  sensitive credentials.
                </p>

              </div>

            </div>


            {/* ======================================= */}
            {/* SERVER RESPONSE                         */}
            {/* ======================================= */}

            {notice && (

              <div
                className={`
                  support-form-notice
                  ${
                    submitStatus === "success"
                      ? "support-form-notice-success"
                      : ""
                  }
                  ${
                    submitStatus === "error"
                      ? "support-form-notice-error"
                      : ""
                  }
                `}
                role={
                  submitStatus === "error"
                    ? "alert"
                    : "status"
                }
              >

                <strong>

                  {submitStatus === "success"
                    ? "Problem Report Submitted"
                    : "Unable to Submit Report"}

                </strong>

                <p>
                  {notice}
                </p>


                {ticketNumber && (

                  <p className="support-ticket-number">

                    Ticket Number:{" "}

                    <strong>
                      {ticketNumber}
                    </strong>

                  </p>

                )}

              </div>

            )}


            {/* ======================================= */}
            {/* SUBMIT                                  */}
            {/* ======================================= */}

            <button
              type="submit"
              className="support-submit-button"
              disabled={isSubmitting}
            >

              {isSubmitting
                ? "Submitting..."
                : "Submit Problem Report"}

            </button>

          </form>

        </section>


        <div className="support-help-link">

          <p>
            Looking for general help instead?
          </p>

          <Link to="/help">
            Visit the Help Center →
          </Link>

        </div>

      </div>

    </main>
  );
};


export default ReportProblem;